import { FretboardModel } from '@fretgenius/core/fretboard-model';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { FretboardInitialConfig, FretboardConfigState, FretboardStorageConfig } from '../types';
import { createFretboardActions } from './actions';
import { computeInitialConfig } from './initialConfig';
import { createFretboardPersistOptions } from './persistOptions';

/**
 * Fretboard Configuration Store (useFretboardConfigStore.ts)
 * =======================================================
 *
 * Central store for managing fretboard configuration and state.
 * Handles all aspects of fretboard display and behavior.
 *
 * Configuration Areas:
 * ------------------
 * 1. Instrument Setup
 *    - Type (guitar, bass)
 *    - Tuning
 *    - Number of frets
 *
 * 2. Visual Settings
 *    - Visible fret range
 *    - Height ratio
 *    - Note circle size
 *
 * 3. Layout Options
 *    - Handedness (left/right)
 *    - Equal temperament (logarithmic) vs. regular (linear) fret spacing
 *    - Responsive sizing
 *
 * Usage Examples:
 * -------------
 * ```tsx
 * // Reading values
 * const MyComponent = () => {
 *   const numFrets = useFretboardConfigStore(state => state.numFrets);
 *   const isLeftHanded = useFretboardConfigStore(state => state.isLeftHanded);
 *   return <div>Frets: {numFrets}</div>;
 * };
 *
 * // Updating settings
 * const ConfigPanel = () => {
 *   const setNumFrets = useFretboardConfigStore(state => state.setNumFrets);
 *   const setTuning = useFretboardConfigStore(state => state.setTuningId);
 *
 *   return (
 *     <div>
 *       <button onClick={() => setNumFrets(24)}>24 Frets</button>
 *       <button onClick={() => setTuning('drop-d')}>Drop D</button>
 *     </div>
 *   );
 * };
 * ```
 */

/**
 * Creates a fretboard configuration store with a custom storage implementation.
 * This allows for using different storage backends on different platforms.
 *
 * @param options - Storage configuration
 * @param initialConfig - Optional initial configuration values to override defaults
 * @returns A Zustand store with fretboard configuration state
 *
 * @example
 * ```ts
 * // Web usage with localStorage
 * import { createJSONStorage } from 'zustand/middleware';
 * export const useFretboardConfigStore = createFretboardConfigStore({
 *   storage: createJSONStorage(() => localStorage)
 * });
 *
 * // With initial config
 * export const useFretboardConfigStore = createFretboardConfigStore({
 *   storage: createJSONStorage(() => localStorage)
 * }, {
 *   instrumentType: InstrumentType.Bass,
 *   isLeftHanded: true
 * });
 *
 * // Mobile usage with AsyncStorage
 * import AsyncStorage from '@react-native-async-storage/async-storage';
 * import { createJSONStorage } from 'zustand/middleware';
 *
 * export const useFretboardConfigStore = createFretboardConfigStore({
 *   storage: createJSONStorage(() => AsyncStorage)
 * });
 * ```
 */
export const createFretboardConfigStore = (
  options: FretboardStorageConfig,
  initialConfig?: FretboardInitialConfig
) => {
  const { storage, reportErrorFn } = options;

  return create<FretboardConfigState>()(
    persist(
      (set, get) => {
        const init = computeInitialConfig(initialConfig, reportErrorFn);
        const initialFretboardModel = new FretboardModel({
          instrumentType: init.instrumentType,
          tuningId: init.tuningId,
          numFrets: init.numFrets,
        });

        return {
          instrumentType: init.instrumentType,
          tuningId: init.tuningId,
          numFrets: init.numFrets,
          heightRatio: init.heightRatio,
          fretboardModel: initialFretboardModel,
          useEqualTemperament: init.useEqualTemperament,
          useTrapezoidScaling: init.useTrapezoidScaling,
          noteCircleScale: init.noteCircleScale,
          isLeftHanded: init.isLeftHanded,
          accidentalPreference: init.accidentalPreference,
          fretWidthBase: init.fretWidthBase,
          showFretMarkerLabels: init.showFretMarkerLabels,
          ...createFretboardActions(set, get, reportErrorFn),
        };
      },
      createFretboardPersistOptions(storage, reportErrorFn)
    )
  );
};
